import React, { useState } from "react";
import styled from "styled-components";
import Papa from "papaparse";
import { useSelector } from "react-redux";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import { showErrorModal } from "../../../util/general";
import { API_ENDPOINT, FILES_BASE_URL } from "../../../util/constant";

const Container = styled.div`
  background: #F8F8F8;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0,0,0,0.05);
  padding: 1.5em;
  max-width: 800px;
  margin: auto;
`;

const Title = styled.h1`
  font-size: 1.8em;
  margin-bottom: 1em;
  color: ${({ colors }) => colors.labelText};
`;

const RadioGroup = styled.div`
  margin-bottom: 1em;
  label {
    display: block;
    margin-bottom: 0.5em;
    font-size: 1em;
    color: ${({ colors }) => colors.labelText};
    strong { font-weight: 600; }
  }
  input { margin-right: 0.5em; }
`;

const CheckboxGroup = styled.div`
  margin-bottom: 1.5em;
  label {
    display: flex;
    align-items: center;
    font-size: 1em;
    color: ${({ colors }) => colors.labelText};
    input { margin-right: 0.5em; }
  }
`;

const Note = styled.p`
  font-size: 0.9em;
  margin: 1em 0;
  color: ${({ colors }) => colors.labelText};
`;

const MappingSection = styled.div`
  margin: 1.5em 0;
  padding: 1em;
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 6px;
  background: ${({ colors }) => colors.cardBackground};
`;

const MappingRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }
`;

const MappingLabel = styled.div`
  font-weight: 600;
  color: ${({ colors }) => colors.labelText};
`;

const MappingHint = styled.div`
  font-size: 0.85em;
  color: ${({ colors }) => colors.helpText || "#4b5563"};
`;

const MappingSelect = styled.select`
  width: 100%;
  min-height: 36px;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid ${({ colors }) => colors.border};
  background: white;
  font-size: 0.95em;
`;

const SampleValue = styled.div`
  font-size: 0.9em;
  color: ${({ colors }) => colors.labelText};
  background: #f8fafc;
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 4px;
  padding: 8px;
  min-height: 36px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 250px;
  overflow-x: auto;
  white-space: nowrap;
`;

const FileRow = styled.div`
  margin-bottom: 1.5em;
  label {
    font-weight: 600;
    color: ${({ colors }) => colors.labelText};
    display: block;
    margin-bottom: 0.5em;
  }
  input[type="file"] { font-size: 1em; }
`;

const SubmitRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.6em 1.2em;
  font-size: 1em;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ primary, colors }) =>
    primary ? colors.buttonBackground : colors.cardBackground};
  color: ${({ primary }) => (primary ? "white" : "inherit")};
  border: ${({ primary, colors }) =>
    primary ? "none" : `1px solid ${colors.border}`};
  &:hover {
    background: ${({ primary, colors }) =>
      primary ? colors.buttonHoverBackground : colors.buttonBackground};
    color: white;
  }
`;

const SampleLink = styled.a`
  font-size: 0.9em;
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

// Toggle this to true to re-enable the column mapping UI once we want it again.
const COLUMN_MAPPING_ENABLED = true;

const normalizeHeaderKey = (key = "") =>
  key
    .replace(/[“”]/g, "\"")
    .replace(/\s+/g, " ")
    .replace(/"/g, "")
    .trim()
    .toLowerCase();

const normalizePhone = value => {
  const digits = (value || "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
};

const normalizeCsvContent = text =>
  text
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");

const COL_LABELS = {
  SCHOOL_NAME:
    "Full School Name as stated on District Web Site / Pre-schools, administrators, adult schools, and early childhood, use the School District name",
  EMAIL: "E-mail Address ",
  GRADES:
    "Grade Level Administrators, adult- schools, online and special schools = K-12 (Comma separated)",
  NUM_STUDENTS: [
    "Estimated # of Students to participate in i-SAFE per educator",
    "Estimated # of Students to participate in ISAFE per educator",
  ],
  START_DATE: "Start Date    (8 digits)",
  END_DATE: "End Date         (8 digits)",
  SCHOOL_LEVEL:
    "School Level      (i.e., Elementary, Middle, High, K-12, K-8, 6-12, or Administrator)",
  SCHOOL_PHONE: "School Phone #†    No ( ) and no EXT.",
  SCHOOL_ADDRESS: "School Address ",
  SCHOOL_CITY: "School City  ",
  SCHOOL_STATE: "School State ",
  SCHOOL_ZIP: "School Zip ",
  SCHOOL_COUNTRY: "School Country",
  SCHOOL_COUNTY: "School County ",
  SCHOOL_DISTRICT:
    "School District Name as stated on District Web site     (If it's a private school please state 'Private')",
};

const FIELD_DEFS = [
  { key: "first_name", label: "First name", required: false },
  { key: "last_name", label: "Last name", required: false },
  { key: "email", label: "Email address", required: true },
  { key: "start_date", label: "Start date", required: true },
  { key: "end_date", label: "End date", required: true },
  { key: "grades", label: "Grade levels", required: true },
  { key: "num_of_students", label: "# of students", required: true },
  { key: "org_name", label: "School name", required: true },
  { key: "school_level", label: "School level", required: true },
  { key: "district", label: "District name", required: false },
  { key: "phone", label: "School phone", required: false },
  { key: "street", label: "School address", required: false },
  { key: "city", label: "School city", required: false },
  { key: "state", label: "School state", required: false },
  { key: "zip", label: "School zip", required: false },
  { key: "country", label: "School country", required: false },
  { key: "county", label: "School county", required: false },
];

const FIELD_SYNONYMS = {
  first_name: ["first name", "first", "fname", "given name"],
  last_name: ["last name", "last", "lname", "surname", "family name"],
  email: [COL_LABELS.EMAIL, "email", "e-mail address"],
  start_date: [COL_LABELS.START_DATE, "start date", "start"],
  end_date: [COL_LABELS.END_DATE, "end date", "end"],
  org_name: [COL_LABELS.SCHOOL_NAME, "school name", "school", "organization"],
  district: [COL_LABELS.SCHOOL_DISTRICT, "district"],
  num_of_students: [
    ...COL_LABELS.NUM_STUDENTS,
    "students",
    "num students",
    "num of students",
    "number of students",
  ],
  grades: [COL_LABELS.GRADES, "grades", "grade level"],
  school_level: [COL_LABELS.SCHOOL_LEVEL, "school level", "level"],
  phone: [COL_LABELS.SCHOOL_PHONE, "phone"],
  street: [COL_LABELS.SCHOOL_ADDRESS, "address", "street"],
  city: [COL_LABELS.SCHOOL_CITY, "city"],
  state: [COL_LABELS.SCHOOL_STATE, "state"],
  zip: [COL_LABELS.SCHOOL_ZIP, "zip"],
  country: [COL_LABELS.SCHOOL_COUNTRY, "country"],
  county: [COL_LABELS.SCHOOL_COUNTY, "county"],
};

const toIsoDate = (year, month, day) => {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return "";

  const candidate = new Date(Date.UTC(y, m - 1, d));
  if (
    candidate.getUTCFullYear() !== y ||
    candidate.getUTCMonth() !== m - 1 ||
    candidate.getUTCDate() !== d
  ) {
    return "";
  }

  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

const normalizeDateValue = value => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return toIsoDate(isoMatch[1], isoMatch[2], isoMatch[3]);
  }

  const compactDigits = raw.replace(/\D/g, "");
  // Only treat as compact YYYYMMDD if the original input contained digits only.
  if (compactDigits === raw && /^\d{8}$/.test(compactDigits)) {
    const year = compactDigits.slice(0, 4);
    const month = compactDigits.slice(4, 6);
    const day = compactDigits.slice(6, 8);
    return toIsoDate(year, month, day);
  }

  const ymdMatch = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    return toIsoDate(y, m, d);
  }

  const mdyMatch = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  if (mdyMatch) {
    const [, first, second, yearInput] = mdyMatch;
    const year = yearInput.length === 2 ? `20${yearInput}` : yearInput;
    const firstNum = Number(first);
    const secondNum = Number(second);
    if (firstNum > 12 && secondNum <= 12) {
      return toIsoDate(year, secondNum, firstNum);
    }
    if (secondNum > 12 && firstNum <= 12) {
      return toIsoDate(year, firstNum, secondNum);
    }
    return toIsoDate(year, firstNum, secondNum);
  }

  const cleaned = raw
    .replace(/(\d+)(st|nd|rd|th)/gi, "$1")
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const parsed = new Date(cleaned);
  if (!Number.isNaN(parsed.getTime())) {
    return toIsoDate(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  }

  return "";
};

const guessMapping = headers => {
  const normalizedHeaders = headers.map(h => ({
    raw: h,
    normalized: normalizeHeaderKey(h),
  }));

  const mapping = {};
  FIELD_DEFS.forEach(def => {
    const synonyms = (FIELD_SYNONYMS[def.key] || []).map(normalizeHeaderKey);
    const match = normalizedHeaders.find(h =>
      synonyms.some(label => h.normalized === normalizeHeaderKey(label))
    );
    if (match) {
      mapping[def.key] = match.raw;
    }
  });
  return mapping;
};

const readCell = (row, header) => {
  if (!header) return "";
  if (header in row) return row[header];
  const normalizedTarget = normalizeHeaderKey(header);
  const normalizedRow = Object.entries(row || {}).reduce((acc, [key, value]) => {
    acc[normalizeHeaderKey(key)] = value;
    return acc;
  }, {});
  return normalizedRow[normalizedTarget] ?? "";
};

export default function ImportTeacherList({ colors, onBack }) {
  const currentSubscription =
    useSelector(s => s.auth.selected_subscriptions) || {};
  const customerId = currentSubscription?.customer_id;
  const invoiceId = currentSubscription?.invoice_id
    ? String(currentSubscription.invoice_id)
    : "";
  const [autoEmail, setAutoEmail] = useState("no");
  // and admins?
  const [sendEmailToAdmin, setSendEmailToAdmin] = useState(false);

  const [file, setFile]                 = useState(null);
  const [parsedUsers, setParsedUsers]   = useState([]);
  const [csvHeaders, setCsvHeaders]     = useState([]);
  const [csvRows, setCsvRows]           = useState([]);
  const [csvSampleRow, setCsvSampleRow] = useState(null);
  const [mapping, setMapping]           = useState({});
  const [fileLocked, setFileLocked]     = useState(false);
  const [parseError, setParseError]     = useState("");
  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [resultOpen, setResultOpen]     = useState(false);
  const [resultStatus, setResultStatus] = useState("Success");
  const [resultMessage, setResultMessage] = useState("");
  const [resultErrors, setResultErrors]   = useState([]);

  const buildUsersFromRows = rows => {
    const users = rows
      ?.map(rawRow => {
        const email = readCell(rawRow, mapping.email).trim();
        if (!email) return null;

        const firstRaw = readCell(rawRow, mapping.first_name).trim();
        const lastRaw  = readCell(rawRow, mapping.last_name).trim();
        const first = firstRaw;
        const last = lastRaw;
        const grades = readCell(rawRow, mapping.grades)
          .split(/[;,]/)
          .map(g => g.trim())
          .filter(Boolean)
          .map(g => g.toUpperCase());
        const uniqueGrades = Array.from(new Set(grades));

        const rawOrgName = readCell(rawRow, mapping.org_name).trim();
        const orgName = rawOrgName || readCell(rawRow, mapping.district).trim();
        const numStudentsRaw = String(readCell(rawRow, mapping.num_of_students) ?? "").trim();
        const parsedNumStudents = Number(numStudentsRaw);

        return {
          customer_id: Number(customerId) || undefined,
          invoice_id:  invoiceId,
          email,
          num_of_students: Number.isFinite(parsedNumStudents) ? parsedNumStudents : undefined,
          first_name: first,
          last_name:  last,
          org_name: orgName,
          phone: normalizePhone(readCell(rawRow, mapping.phone)),
          street: readCell(rawRow, mapping.street).trim(),
          city: readCell(rawRow, mapping.city).trim(),
          state: readCell(rawRow, mapping.state).trim().toUpperCase(),
          zip: readCell(rawRow, mapping.zip).trim(),
          country: readCell(rawRow, mapping.country).trim().toUpperCase(),
          county: readCell(rawRow, mapping.county).trim(),
          district: readCell(rawRow, mapping.district).trim(),
          start_date: normalizeDateValue(readCell(rawRow, mapping.start_date)),
          end_date:   normalizeDateValue(readCell(rawRow, mapping.end_date)),
          school_level: readCell(rawRow, mapping.school_level).trim().toUpperCase(),
          grades: uniqueGrades,
        };
      })
      .filter(Boolean);

    const dedupedUsers = [];
    const seenEmails = new Set();
    users.forEach(user => {
      const key = user.email.toLowerCase();
      if (!seenEmails.has(key)) {
        seenEmails.add(key);
        dedupedUsers.push(user);
      }
    });
    return dedupedUsers;
  };

  // parse on “Import”
  const handleSubmit = e => {
    e.preventDefault();
    if (!customerId || !invoiceId) {
      showErrorModal("Missing subscription context. Please select a subscription before importing.");
      return;
    }
    if (!csvRows.length) {
      showErrorModal("Please select and parse a CSV file before importing.");
      return;
    }

    const missing = FIELD_DEFS.filter(def => def.required && !mapping[def.key]);
    if (missing.length) {
      showErrorModal(
        `Missing required columns: ${missing.map(m => m.label).join(", ")}. Please use the standard import headers.`
      );
      return;
    }

    const users = buildUsersFromRows(csvRows);
    setParsedUsers(users);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    // derive the two teacher flags
    const payload = {
      users: parsedUsers,
      sendTeacherEmailNew: autoEmail === "new" ? 1 : 0,
      send_email_to_admin: sendEmailToAdmin ? 1 : 0,
      send_email_to_all_teachers: autoEmail === "all" ? 1 : 0,
    };

    if (!payload.users?.length) {
      setResultStatus("Failed");
      setResultMessage("No valid teacher rows found in the CSV.");
      setResultErrors([]);
      setResultOpen(true);
      return;
    }

    API.importTeacherList(payload)
      .then(resp => {
        let errs = [];
        if (Array.isArray(resp.errors)) {
          errs = resp.errors;
        } else if (resp.errors && typeof resp.errors === "object") {
          errs = Object.keys(resp.errors);
        }
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setResultStatus(status);
        setResultMessage(resp.message);
        setResultErrors(errs);
        setResultOpen(true);
      })
      .catch(err => {
        setResultStatus("Failed");
        setResultMessage(err.message || "Import failed");
        setResultErrors([]);
        setResultOpen(true);
      });
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setResultStatus("Success");
    onBack();
  };

  const handleFileSelected = async e => {
    const nextFile = e.target.files?.[0];
    if (!nextFile) return;
    if (fileLocked) {
      showErrorModal("You can only select one file for this import. Reload the page to start over.");
      return;
    }

    setParseError("");
    setFile(nextFile);

    let normalizedCsv = "";
    try {
      const text = await nextFile.text();
      normalizedCsv = normalizeCsvContent(text);
    } catch (err) {
      showErrorModal("Unable to read the CSV file. Please try again.");
      return;
    }

    Papa.parse(normalizedCsv, {
      header: true,
      skipEmptyLines: "greedy",
      complete: ({ data, errors, meta }) => {
        if (errors.length) {
          setParseError("CSV parse error: " + errors[0].message);
          return;
        }
        const headers = Array.isArray(meta?.fields) ? meta.fields : Object.keys(data?.[0] || {});
        if (!headers.length) {
          setParseError("No headers found in the CSV.");
          return;
        }
        const sampleRow = (data || []).find(row =>
          Object.values(row || {}).some(val => (val || "").toString().trim() !== "")
        );

        setCsvHeaders(headers);
        setCsvSampleRow(sampleRow || null);
        setCsvRows(data || []);
        setMapping(prev => ({ ...prev, ...guessMapping(headers) }));
        setFileLocked(true);
      },
    });
  };

   const handleDownloadSample = async e => {
    e.preventDefault();
    const sampleUrl = `${FILES_BASE_URL}/ISAFE_STAT_Sheet.csv`;
    try {
      const resp = await fetch(sampleUrl);
      if (!resp.ok) throw new Error(`Status ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ImportTeachersSTATSample.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Sample CSV download failed:", err);
      showErrorModal("Unable to download sample CSV.");
    }
  };
  return (
    <Container>
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <Title colors={colors}>Import Teachers from CSV</Title>

        <RadioGroup colors={colors}>
          <label>
            <input
              type="radio"
              name="auto_email"
              value="no"
              checked={autoEmail === "no"}
              onChange={() => setAutoEmail("no")}
            />
            <strong>Do not send</strong> auto-email notifications to educators.
          </label>
          <label>
            <input
              type="radio"
              name="auto_email"
              value="all"
              checked={autoEmail === "all"}
              onChange={() => setAutoEmail("all")}
            />
            <strong>Send</strong> auto-emails for <strong>all</strong> implementations.
          </label>
          <label>
            <input
              type="radio"
              name="auto_email"
              value="new"
              checked={autoEmail === "new"}
              onChange={() => setAutoEmail("new")}
            />
            <strong>Send</strong> auto-emails for <strong>new</strong> implementations only.
          </label>
        </RadioGroup>

        <CheckboxGroup colors={colors}>
          <label>
            <input
              type="checkbox"
              checked={sendEmailToAdmin}
              onChange={e => setSendEmailToAdmin(e.target.checked)}
            />
            Send auto-email notifications to administrators?
          </label>
        </CheckboxGroup>

        <Note colors={colors}>
          Accounts will be created and users can set their password at:<br/>
          {`${API_ENDPOINT}/password/request`}
        </Note>

        <FileRow colors={colors}>
          <label htmlFor="teacherCsv">Select CSV file (max 50 MB):</label>
          <input
            id="teacherCsv"
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileSelected}
            disabled={fileLocked}
            required
          />
          {parseError && (
            <MappingHint colors={colors} style={{ color: "#b91c1c", marginTop: "8px" }}>
              {parseError}
            </MappingHint>
          )}
        </FileRow>

        {COLUMN_MAPPING_ENABLED && csvHeaders.length > 0 && (
          <MappingSection colors={colors}>
            <h3 style={{ marginTop: 0 }}>Map your columns</h3>
            <MappingHint colors={colors}>
              We detected the columns in your file. Match them to the fields we need. An example value from your file is shown for context.
            </MappingHint>
            <div style={{ marginTop: "12px" }}>
              {FIELD_DEFS.map(def => {
                const currentSelection = mapping[def.key] || "";
                const sampleValue = String(
                  readCell(csvSampleRow || {}, currentSelection) ?? ""
                ).trim();
                return (
                  <MappingRow key={def.key} colors={colors}>
                    <div>
                      <MappingLabel colors={colors}>
                        {def.label}
                        {def.required ? " *" : ""}
                      </MappingLabel>
                      <MappingHint colors={colors}>
                        {def.required ? "Required" : "Optional"}
                      </MappingHint>
                    </div>
                    <MappingSelect
                      colors={colors}
                      value={currentSelection}
                      onChange={e =>
                        setMapping(prev => ({
                          ...prev,
                          [def.key]: e.target.value || undefined,
                        }))
                      }
                    >
                      <option value="">
                        {def.required ? "Select a column" : "Do not import"}
                      </option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </MappingSelect>
                    <SampleValue colors={colors}>
                      {currentSelection ? sampleValue : "—"}
                    </SampleValue>
                  </MappingRow>
                );
              })}
            </div>
          </MappingSection>
        )}

        <SubmitRow>
          <Button colors={colors} onClick={onBack} type="button">
            Back
          </Button>
          <Button colors={colors} primary type="submit">
            Import
          </Button>
        </SubmitRow>

        <p style={{ marginTop: "1em", textAlign: "center" }}>
          <SampleLink colors={colors} onClick={handleDownloadSample}>
            Download Sample CSV
          </SampleLink>
        </p>
      </form>

      {/* confirm before import */}
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Import"
        message={`You’re about to import ${parsedUsers.length} teacher${parsedUsers.length !== 1 ? "s" : ""}. Proceed?`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />

      {/* result of import */}
      <ConfirmationModal
        show={resultOpen}
        title={resultStatus === "Success" ? "Import Complete" : "Import Failed"}
        message={
          resultMessage +
          (resultErrors.length
            ? `\n\nErrors importing the following teachers:\n${resultErrors.join("\n")}`
            : ""
          )
        }
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={resultStatus}
        single
      />
    </Container>
  );
}
